import { Badge, type SkillCategory } from '@/components/ui-kit/badge';
import { cn } from '@/lib/utils';

export interface SkillChip {
  name: string;
  /** The chip's hue: its skill sub-category (Frontend, Backend, ...); none = a plain outline chip. */
  tone?: SkillCategory;
  /** Title of the sub-category and the proficiency, shown together as the chip's tip. */
  subCategory?: string;
  level?: string;
}

interface SkillChipsProps {
  skills: SkillChip[];
  /** How many chips show before "+N" (prototype `skillChips`, default 2). */
  max?: number;
  className?: string;
}

/**
 * A person's skills as chips on one line (prototype `skillChips`): the first `max` coloured by their
 * sub-category, then a "+N" outline badge whose tip lists the rest. Nothing to show reads as "—".
 */
export const SkillChips = ({ skills, max = 2, className }: SkillChipsProps) => {
  if (skills.length === 0) return <span className="text-muted-foreground">—</span>;
  const rest = skills.slice(max);
  const restNames = rest.map((skill) => skill.name).join(', ');
  return (
    <div className={cn('flex flex-nowrap items-center gap-1', className)}>
      {skills.slice(0, max).map((skill) => (
        <Badge
          key={skill.name}
          skill={skill.tone}
          variant="outline"
          title={[skill.subCategory, skill.level].filter(Boolean).join(' · ') || undefined}
        >
          {skill.name}
        </Badge>
      ))}
      {rest.length > 0 && (
        <Badge
          variant="outline"
          className="cursor-default text-muted-foreground"
          tabIndex={0}
          title={restNames}
          aria-label={`${rest.length} more: ${restNames}`}
        >
          +{rest.length}
        </Badge>
      )}
    </div>
  );
};
